/**
 * Drive Navigation Command
 *
 * Browser-side handler for the `space_drive_navigate` broadcastCmd fired by
 * the headless agent via tool-runtime/src/tools/space_ui.ts `drive_navigate`.
 *
 * Flow:
 *   1. Agent calls space_drive_navigate({ view?, folderEntityId?, fileEntityId? })
 *   2. tool-runtime broadcasts "space_drive_navigate" on the org Y.Doc pendingCmds map
 *   3. orgYDocStore observer picks up the command and routes to commandRegistryStore
 *   4. commandRegistryStore executes this SelfContainedCommand
 *   5. Handler resolves the Drive store for the open Drive window and applies navigation
 *
 * Logging prefix: [DriveNav]
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import useSpaceStore from '../../stores/spaceStore';
import { useSpatialUIStore } from '../../stores/spaceSpatialUIStore';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('DRIVE_NAV');

const DRIVE_VIEW_MODES: Record<string, string> = {
  files:        'files',
  integrations: 'integrations',
  recents:      'recents',
  trash:        'trash',
  agents:       'agents',
  apps:         'apps',
  tools:        'tools',
};

const driveNavigateCommand: SelfContainedCommand = {
  id: 'space_drive_navigate',
  description: 'Navigate the Drive app to a specific folder, file entity, or view.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'view',
        type: 'string',
        description: 'Named Drive view: files | integrations | recents | trash | agents | apps | tools',
        required: false,
      },
      {
        name: 'folderEntityId',
        type: 'string',
        description: 'Entity ID of a folder to navigate into',
        required: false,
      },
      {
        name: 'fileEntityId',
        type: 'string',
        description: 'Entity ID of a file to select in the current folder',
        required: false,
      },
    ],
  },

  examples: { good: [], bad: [] },
  performance: { expectedDuration: 300, complexity: 'low' },

  handler: async (params: any, _context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { view, folderEntityId, fileEntityId } = params ?? {};
    const startTime = Date.now();

    log.info(`[DriveNav] received view=${view ?? 'none'} folderEntityId=${folderEntityId ?? 'none'} fileEntityId=${fileEntityId ?? 'none'}`);

    try {
      // Find the open Drive window
      const { windows, actions, bringWindowToFront } = useSpaceStore.getState();
      const driveEntry = Object.entries(windows).find(
        ([, w]: any) => w.appType === 'entityBrowser2'
      );

      if (!driveEntry) {
        log.warn('[DriveNav] No Drive window open — ignoring navigate command');
        return createErrorResult('Drive not open', 'No Drive window is currently open', 'validation');
      }

      const [driveWindowId] = driveEntry;
      bringWindowToFront(driveWindowId);
      useSpatialUIStore.getState().bringWindowToFront(driveWindowId);
      actions.setActiveWindow(driveWindowId);

      // Get the RiverDriveApp store for this window
      const spaceId: string = (windows[driveWindowId] as any)?.spaceId || '';
      const { getRiverDriveAppStore } = await import('../../apps/RiverDriveApp/store/riverDriveAppStore');
      const driveStore = getRiverDriveAppStore(driveWindowId, spaceId).getState();

      // Apply view change
      if (view && DRIVE_VIEW_MODES[view]) {
        driveStore.setActiveView({ mode: view as any });
        log.info(`[DriveNav] applied view=${view}`);
      }

      // Navigate to folder
      if (folderEntityId) {
        // Try to get folder name from entity store
        let folderName = 'Folder';
        try {
          const { default: useEntityStore } = await import('../../stores/entityStore');
          const entity = useEntityStore.getState().entities.find(
            (e: any) => e._id === folderEntityId || e.id === folderEntityId
          );
          if (entity?.name) folderName = entity.name;
        } catch { /* non-fatal */ }

        driveStore.navigateToFolder(folderEntityId, folderName);
        log.info(`[DriveNav] applied folderEntityId=${folderEntityId} name="${folderName}"`);
      }

      // Select file entity
      if (fileEntityId) {
        driveStore.selectEntity(fileEntityId);
        log.info(`[DriveNav] applied fileEntityId=${fileEntityId}`);
      }

      const parts: string[] = [];
      if (view) parts.push(`view="${view}"`);
      if (folderEntityId) parts.push(`folder=${folderEntityId}`);
      if (fileEntityId) parts.push(`file=${fileEntityId}`);

      return createSuccessResult(
        `Drive navigated${parts.length ? ': ' + parts.join(', ') : ''}`,
        { windowId: driveWindowId },
        { metadata: { executionTime: Date.now() - startTime } }
      );
    } catch (error: any) {
      log.error('[DriveNav] Error:', error);
      return createErrorResult('Drive navigation failed', error.message || 'Unknown error', 'execution', {
        metadata: { executionTime: Date.now() - startTime },
      });
    }
  },
};

export const driveNavigationCommands: SelfContainedCommand[] = [driveNavigateCommand];
